import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { CompanyService } from 'src/core/services/company.service';
import { PermissionsService } from 'src/core/services/permissions.service';
import {
  collectCompaniesMissingSageCustomerCode,
  MissingSageCustomerRow,
} from 'src/core/utils/missing-sage-customers';
import {
  readSageCustomerCode,
  SAGE_CUSTOMER_CODE_HELPER,
  SAGE_CUSTOMER_CODE_MISSING_WARNING,
  sageCustomerCodeForApi,
} from 'src/core/utils/sage-customer-code';

@Component({
  selector: 'app-missing-sage-customer-codes',
  templateUrl: './missing-sage-customer-codes.component.html',
  styleUrls: ['./missing-sage-customer-codes.component.scss'],
  standalone: false,
})
export class MissingSageCustomerCodesComponent implements OnInit, OnDestroy {
  readonly helper = SAGE_CUSTOMER_CODE_HELPER;
  readonly missingWarning = SAGE_CUSTOMER_CODE_MISSING_WARNING;

  displayedColumns: string[] = ['company_id', 'name', 'sage_customer_code'];

  rows: MissingSageCustomerRow[] = [];
  visibleRows: MissingSageCustomerRow[] = [];
  pagedRows: MissingSageCustomerRow[] = [];
  searchInput = new UntypedFormControl('');
  pageIndex = 0;
  pageSize = 10;
  readonly pageSizeOptions = [10, 25, 50];
  loading = false;
  companiesDone = 0;
  companyTotal = 0;
  incomplete = false;
  savingCompanyId: number | null = null;

  private _cancelled = false;
  private _draftCodes = new Map<number, string>();

  constructor(
    private _companyService: CompanyService,
    public readonly perm: PermissionsService
  ) {}

  ngOnInit(): void {
    this.searchInput.valueChanges.subscribe(() => this.applyFilter(true));
    this.load();
  }

  ngOnDestroy(): void {
    this._cancelled = true;
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePage();
  }

  private applyFilter(resetPage = false): void {
    const query = String(this.searchInput.value ?? '').trim().toLowerCase();
    if (!query) {
      this.visibleRows = this.rows;
    } else {
      this.visibleRows = this.rows.filter((row) => {
        return String(row.companyId).includes(query) || row.name.toLowerCase().includes(query);
      });
    }
    if (resetPage) this.pageIndex = 0;
    this.updatePage();
  }

  /** Show one page of the filtered companies. Typed codes stay in the draft map. */
  private updatePage(): void {
    const last = Math.max(0, Math.ceil(this.visibleRows.length / this.pageSize) - 1);
    if (this.pageIndex > last) this.pageIndex = last;
    const start = this.pageIndex * this.pageSize;
    this.pagedRows = this.visibleRows.slice(start, start + this.pageSize);
  }

  codeFor(row: MissingSageCustomerRow): string {
    return this._draftCodes.get(row.companyId) ?? '';
  }

  onCodeInput(row: MissingSageCustomerRow, value: string): void {
    this._draftCodes.set(row.companyId, value);
  }

  async load(): Promise<void> {
    this.loading = true;
    this.incomplete = false;
    this.companiesDone = 0;
    this.companyTotal = 0;
    this.rows = [];
    this.visibleRows = [];
    this.pagedRows = [];
    this.pageIndex = 0;

    try {
      const scan = await collectCompaniesMissingSageCustomerCode({
        listCompanies: (page, limit) => this._companyService.getCompaniesPage(page, limit, 'both'),
        onProgress: (state) => {
          if (this._cancelled) return;
          this.companiesDone = state.companiesDone;
          this.companyTotal = state.companyTotal;
        },
        isCancelled: () => this._cancelled,
      });

      if (this._cancelled) return;
      this.rows = scan.rows;
      this.applyFilter(true);
      this.companiesDone = scan.companiesDone;
      this.companyTotal = scan.companyTotal;
      this.incomplete = scan.incomplete;
    } finally {
      if (!this._cancelled) this.loading = false;
    }
  }

  async save(row: MissingSageCustomerRow): Promise<void> {
    if (!this.perm.canAddOrEditIndividualOrCompany() || this.savingCompanyId != null) return;
    if (row.company.id == null) return;

    const sageCustomerCode = sageCustomerCodeForApi(this.codeFor(row));
    const payload = {
      ...this._companyService.constructionCompanyEditpayload(row.company, row.company.distributor),
      delivery_fee_exempt: row.company.delivery_fee_exempt ?? null,
      sage_customer_code: sageCustomerCode,
    };

    this.savingCompanyId = row.companyId;
    try {
      const saved = await this._companyService.editCompany(row.company.id, payload);
      if (!saved) return;
      const fresh = await this._companyService.getCompany(row.company.id);
      if (!fresh || readSageCustomerCode(fresh) === '') return;
      this._draftCodes.delete(row.companyId);
      this.rows = this.rows.filter((item) => item.companyId !== row.companyId);
      this.applyFilter();
    } finally {
      this.savingCompanyId = null;
    }
  }
}
